import React, { MouseEvent, useEffect, useState } from "react";
import clsx from "clsx";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { HttpStatusCode } from "axios";

import {
  EmergencySituationType,
  EmergencyStatusType,
  EvacuationStatusType,
} from "../../../types/emergency";

import {
  patchStatus,
  patchUncertStatus,
} from "../../../services/emergencyServices";

import useSimplePopup from "../../../hooks/useSimplePopup";
import useEvacuationManger from "../../../hooks/useEvacuationManager";

import CommonButton from "../../../components/Button";
import CommonPopup from "../../../components/Popup";
import CommonInput from "../../../components/Input/CommonInput";
import SituationStatusButton, {
  SituationStatusButtonProps,
} from "./StatusButton";

import DoneIcon from "../../../assets/img/emergency/emergency-main-done-icon.png";
import HelpIcon from "../../../assets/img/emergency/emergency-main-help-icon.png";
import VacationIcon from "../../../assets/img/emergency/emergency-main-vacation-icon.png";

type Props = {
  situation?: EmergencySituationType;
  handleGps?: () => void;
  refresh?: () => Promise<void>;
};

export default function SituationStatusButtonGroup({
  situation,
  handleGps,
  refresh,
}: Props) {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [isManager, isUser, { user }] = useEvacuationManger();
  const [popup, setPopup] = useState<
    | "help"
    | "helpDetail"
    | "vacation"
    | "evacuation"
    | "updateHelpDetail"
    | "managerStatusUpdate"
    | "managerHelpDetail"
    | ""
  >("");
  const [rescueLocation, setRescueLocation] = useState("");
  const [simplePopupComponent, { open, openError }] = useSimplePopup({});
  const [nextStatus, setNextStatus] = useState<
    EvacuationStatusType | undefined
  >(undefined);

  const buttonList: SituationStatusButtonProps[] = [
    {
      status: "HELP_ME",
      icon: HelpIcon,
      title: "구조요청!",
      description: "도움이 필요하다면 눌러주세요.",
      disabled: !situation,
      onClick: () => {
        setPopup("help");
      },
      activeDescription: `상세위치\n${situation?.rescueLocation ?? "-"}`,
      activeButton: (
        <div
          onClick={() => setPopup("updateHelpDetail")}
          className="flex justify-center items-center bg-brand-neutral-05 text-black font-medium text-opacity-40 border border-brand-gray-100 border-opacity-20 text-[13px] leading-[17px] rounded-[3px] h-[32px] px-2.5 py-[11px] min-w-[97px]"
        >
          상세 위치 수정
        </div>
      ),
    },
    {
      status: "ON_LEAVE_OR_OUT",
      icon: VacationIcon,
      title: "휴가/외근중이에요.",
      onClick: () => {
        setPopup("vacation");
      },
      disabled: !situation,
    },
    {
      status: "EVACUATION_COMPLETE",
      icon: DoneIcon,
      title: "대피 완료 했어요.",
      disabled: !situation,
      onClick: () => {
        setPopup("evacuation");
      },
    },
  ];

  const getUrlFromStatus = (status: EvacuationStatusType) => {
    switch (status) {
      case "HELP_ME":
        return "help-result";
      case "ON_LEAVE_OR_OUT":
        return "vacation-result";
      case "EVACUATION_COMPLETE":
        return "evacuation-result";
      default:
        return "situation";
    }
  };

  const updateMemberStatus = async (
    emergencyStatusParams: EmergencyStatusType,
    onSuccess?: () => void,
    isManagerSelf: boolean = false
  ) => {
    if (situation?.evacuationId) {
      try {
        const result = await patchStatus(
          situation.evacuationId,
          emergencyStatusParams,
          isManagerSelf
        );
        if (result.status === HttpStatusCode.Ok) {
          if (onSuccess) {
            onSuccess();
          } else {
            navigate(
              `/evacuation/${getUrlFromStatus(emergencyStatusParams.status)}`,
              {
                state: {
                  situation:
                    emergencyStatusParams.status === "HELP_ME"
                      ? { ...situation, rescueLocation }
                      : situation,
                  isManager,
                  referer: location?.pathname ?? "/evacuation/situation",
                },
              }
            );
          }
        } else {
          throw new Error("상태 변경에 실패했습니다.");
        }
      } catch (err: any) {
        openError(err, "상태 변경 오류");
        if (err.response?.data.code === -3413 && refresh) {
          refresh();
        }
      }
    } else {
      throw new Error("상황 정보에 비상 대피 ID가 없습니다.");
    }
  };

  const updateUncertStatus = async (
    uuid: string,
    emergencyStatusParams: EmergencyStatusType,
    onSuccess?: () => void
  ) => {
    try {
      const result = await patchUncertStatus(
        uuid as string,
        emergencyStatusParams
      );
      if (result.status === HttpStatusCode.Ok) {
        if (onSuccess) {
          onSuccess();
        } else {
          navigate(
            `/uncert-evacuation/${getUrlFromStatus(
              emergencyStatusParams.status
            )}`,
            {
              state: {
                situation:
                  emergencyStatusParams.status === "HELP_ME"
                    ? { ...situation, rescueLocation }
                    : situation,
                isManager,
                referer: location.pathname ?? "/uncert-evacuation/situation",
              },
            }
          );
        }
      } else {
        throw new Error("상태 변경에 실패했습니다.");
      }
    } catch (err: any) {
      openError(err, "상태 변경 오류");
      if (err.response?.data.code === -3413 && refresh) {
        refresh();
      }
    }
  };

  const handleStatusChange =
    (emergencyStatusParams: EmergencyStatusType) => async (e?: any) => {
      e?.preventDefault();
      if (!params.id && !isUser) return;

      try {
        if (isUser) {
          updateMemberStatus(emergencyStatusParams);
        } else {
          updateUncertStatus(params.id as string, emergencyStatusParams);
        }
      } catch (e: any) {
        openError(e, "상황 정보를 불러오지 못했습니다.");
      }
    };

  const handleMoveToManager = () => {
    if (situation?.userStatus === "CREATED_NO_RESPONSE") {
      setPopup("managerStatusUpdate");
    } else {
      navigate(`/manager/evacuation-manager/${situation?.evacuationId}`);
    }
  };

  const handleHelpDetailConfirm = async () => {
    if ("updateHelpDetail" === popup) {
      handleStatusChange({
        status: "HELP_ME",
        rescueLocation,
      })(undefined);
    } else {
      updateMemberStatus(
        {
          status: "HELP_ME",
          rescueLocation,
        },
        () =>
          navigate(`/manager/evacuation-manager/${situation?.evacuationId}`),
        true
      );
    }
  };

  const handleManagerStatusUpdate = async () => {
    if (nextStatus === undefined) {
      open("상태를 선택해주세요.");
      return;
    }

    if (["ON_LEAVE_OR_OUT", "EVACUATION_COMPLETE"].includes(nextStatus)) {
      updateMemberStatus(
        {
          status: nextStatus,
        },
        () =>
          navigate(`/manager/evacuation-manager/${situation?.evacuationId}`),
        true
      );
    } else {
      setPopup("managerHelpDetail");
    }
  };

  const getOrderedButton = (index: number) => {
    // 활성 상태를 기준으로 정렬
    const sortList = buttonList.sort((a, b) => {
      if (a.status === situation?.userStatus) return -1;
      if (b.status === situation?.userStatus) return 1;
      return 0;
    });

    const isActive =
      index === 0 && situation?.userStatus === sortList[index].status;

    return { ...sortList[index], isActive };
  };

  useEffect(() => {
    if (popup === "updateHelpDetail" && situation?.rescueLocation) {
      setRescueLocation(situation.rescueLocation);
    }
  }, [popup, situation?.rescueLocation]);

  return (
    <>
      <div className="px-5 pb-5">
        <div className="bg-brand-gray-10 bg-opacity-10 p-3 pt-5 flex flex-col gap-[15px]">
          <div className="flex gap-2 justify-between">
            <div className="flex flex-col gap-[5px] font-semibold text-base leading-[19.36px]">
              {situation?.userStatus === "CREATED_NO_RESPONSE" ? (
                <>
                  <span className="break-words text-sm font-semibold leading-[19px]">
                    {
                      "건물 외부 안전한 곳에 있거나 대피 장소로 이동 완료 하셨나요?"
                    }
                  </span>
                  <span className="text-xs leading-4 text-brand-neutral-50">
                    안전 관리자 인원 확인을 위해 선택해주세요.
                  </span>
                </>
              ) : (
                "현재 내 상태"
              )}
            </div>
            {isManager === true && (
              <CommonButton
                variant="secondary"
                className="text-sm leading-[17px] font-medium min-w-[112px] max-h-[33px] rounded-[3px]"
                onClick={handleMoveToManager}
                disabled={!situation}
              >
                관리자 홈 이동
              </CommonButton>
            )}
          </div>
          <div
            className={clsx([
              "flex flex-col",
              {
                "gap-1.5": situation?.userStatus === "CREATED_NO_RESPONSE",
                "gap-2.5": situation?.userStatus !== "CREATED_NO_RESPONSE",
              },
            ])}
            data-testid="situation-status-button-group"
          >
            <div
              data-testid="situation-status-button-first-row"
              className="flex-1 flex"
            >
              <SituationStatusButton {...getOrderedButton(0)} />
            </div>
            {situation?.userStatus !== "CREATED_NO_RESPONSE" && (
              <div className="text-sm leading-[16.94px] font-semibold">
                상태 변경
              </div>
            )}
            <div
              className="flex gap-1.5"
              data-testid="situation-status-button-second-row"
            >
              <SituationStatusButton {...getOrderedButton(1)} isMultiColumn />
              <SituationStatusButton {...getOrderedButton(2)} isMultiColumn />
            </div>
          </div>
        </div>
      </div>

      <CommonPopup
        isOpen={popup === "help"}
        onClose={() => setPopup("")}
        padding={0}
        title="상태 확인"
        unUseHorizonRule
      >
        <div className="flex flex-col items-center">
          <div className="p-5 pt-0">
            <div className="flex flex-col gap-2.5 py-2.5 items-center text-center">
              현재 도움이 필요하신 상태입니까?
            </div>
          </div>
          <div className="flex w-full">
            <CommonButton
              variant="secondary"
              className="rounded-none flex-1 h-bottom-button"
              onClick={() => setPopup("")}
            >
              취소
            </CommonButton>
            <CommonButton
              className="rounded-none flex-1 h-bottom-button"
              onClick={() => setPopup("helpDetail")}
            >
              확인
            </CommonButton>
          </div>
        </div>
      </CommonPopup>
      <CommonPopup
        isOpen={popup === "vacation"}
        onClose={() => setPopup("")}
        padding={0}
        title="상태 확인"
        unUseHorizonRule
      >
        <div className="flex flex-col items-center">
          <div className="p-5 pt-0">
            <div className="flex flex-col gap-2.5 py-2.5 items-center text-center">
              현재 "휴가/외근" 상태입니까?
            </div>
          </div>
          <div className="flex w-full">
            <CommonButton
              variant="secondary"
              className="rounded-none flex-1 h-bottom-button"
              onClick={() => setPopup("")}
            >
              취소
            </CommonButton>
            <CommonButton
              className="rounded-none flex-1 h-bottom-button"
              onClick={handleStatusChange({
                status: "ON_LEAVE_OR_OUT",
              })}
            >
              확인
            </CommonButton>
          </div>
        </div>
      </CommonPopup>
      <CommonPopup
        isOpen={popup === "evacuation"}
        onClose={() => setPopup("")}
        padding={0}
        title="상태 확인"
        unUseHorizonRule
      >
        <div className="flex flex-col items-center">
          <div className="p-5 pt-0">
            <div className="flex flex-col gap-2.5 py-2.5 items-center text-center">
              현재 "대피완료" 상태입니까?
            </div>
          </div>
          <div className="flex w-full">
            <CommonButton
              variant="secondary"
              className="rounded-none flex-1 h-bottom-button"
              onClick={() => setPopup("")}
            >
              취소
            </CommonButton>
            <CommonButton
              className="rounded-none flex-1 h-bottom-button"
              onClick={async (e: MouseEvent<HTMLButtonElement>) => {
                e.preventDefault();
                await handleGps?.();
                setTimeout(() => {
                  setPopup("");
                }, 1000);
              }}
            >
              확인
            </CommonButton>
          </div>
        </div>
      </CommonPopup>
      <CommonPopup
        isOpen={popup === "helpDetail"}
        onClose={() => setPopup("")}
        padding={0}
        title="상세 위치 설명"
        unUseHorizonRule
      >
        <div className="flex flex-col items-center">
          <div className="p-5 pt-0">
            <CommonInput
              placeholder="상세 위치를 설명해주세요. (ex. 3층 회의실)"
              className="p-4 text-base w-[calc(100vw-40px)]"
              type="text"
              value={rescueLocation}
              onChange={(value) => {
                setRescueLocation(value);
              }}
            />
          </div>
          <div className="flex w-full">
            <CommonButton
              variant="secondary"
              className="rounded-none flex-1 h-bottom-button"
              onClick={() => setPopup("")}
            >
              취소
            </CommonButton>
            <CommonButton
              className="rounded-none flex-1 h-bottom-button"
              onClick={handleStatusChange({
                status: "HELP_ME",
                rescueLocation,
              })}
              testId="help-detail-confirm"
            >
              확인
            </CommonButton>
          </div>
        </div>
      </CommonPopup>
      <CommonPopup
        isOpen={["updateHelpDetail", "managerHelpDetail"].includes(popup)}
        onClose={() => setPopup("")}
        padding={0}
        title="상세 위치 수정"
        unUseHorizonRule
      >
        <div className="flex flex-col items-center">
          <div className="p-5 pt-0">
            <CommonInput
              placeholder="상세 위치를 설명해주세요. (ex. 3층 회의실)"
              className="p-4 text-base w-[calc(100vw-40px)]"
              type="text"
              value={rescueLocation}
              onChange={setRescueLocation}
            />
          </div>
          <div className="flex w-full">
            <CommonButton
              variant="secondary"
              className="rounded-none flex-1 h-bottom-button"
              onClick={() => setPopup("")}
            >
              취소
            </CommonButton>
            <CommonButton
              className="rounded-none flex-1 h-bottom-button"
              onClick={handleHelpDetailConfirm}
            >
              확인
            </CommonButton>
          </div>
        </div>
      </CommonPopup>
      <CommonPopup
        isOpen={popup === "managerStatusUpdate"}
        onClose={() => setPopup("")}
        padding={0}
        title="관리자 상태 변경"
        unUseHorizonRule
      >
        <div className="flex flex-col items-center gap-2">
          <div className="whitespace-pre-line text-center">
            {` 관리자 홈 이동을 위해서는\n상태 입력이 필요합니다.`}
          </div>
          <div className="p-5 pt-0 flex flex-col gap-1">
            {buttonList.map((button, index) => (
              <CommonButton
                key={index}
                variant="white"
                className={clsx([
                  "w-[calc(100vw-40px)] !bg-white border",
                  {
                    "border-red-600": button?.status === nextStatus,
                  },
                ])}
                onClick={() => setNextStatus(button?.status)}
                testId={button.status}
              >
                <img src={button.icon} alt={`${button.status}-icon`} />{" "}
                {button.title}
              </CommonButton>
            ))}
          </div>
          <div className="flex w-full">
            <CommonButton
              className="rounded-none flex-1 h-bottom-button"
              onClick={handleManagerStatusUpdate}
              disabled={!nextStatus}
              testId="manager-self-submit"
            >
              확인
            </CommonButton>
          </div>
        </div>
      </CommonPopup>
      {simplePopupComponent}
    </>
  );
}
